import { Grid } from '@planne-software/uni/Grid';
import { uni } from '@planne-software/uni/v2';
import { modalActionsClasses, modalClasses } from '@planne-software/uni/Modal';
import { Box, Chip, accordionDetailsClasses, accordionSummaryClasses, styled } from '@planne-software/uni/mui/material';
import { accordionClasses } from '@planne-software/uni/Accordion';

import { uniTypography } from '@src/fonts/typography/uniTypography';
import { tabletBreakpoint } from '@src/common/utils/mediaQuery';

export const AdditionalInfoChipRoot = styled(Chip)(() => ({
  height: 'auto',
  flexShrink: 0,
  borderRadius: uni('border-radius-round'),
  borderWidth: '1px',
  borderStyle: 'solid',
  '& .MuiChip-label': {
    ...uniTypography('text-sm', 'medium'),
    padding: `${uni('scale-50')} ${uni('scale-200')}`,
  },
  '&[data-tone="success"]': {
    borderColor: uni('border-success-subtle-rest'),
    backgroundColor: uni('bg-success-subtle-rest'),
    color: uni('fg-success-on-color-subtle-rest'),
  },
  '&[data-tone="primary"]': {
    borderColor: uni('border-primary-subtle-rest'),
    backgroundColor: uni('bg-primary-subtle-rest'),
    color: uni('fg-primary-on-color-subtle-rest'),
  },
}));

export const AdditionalInfoRoot = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: uni('spacing-24-16'),
  width: '100%',
  padding: uni('padding-content'),
  backgroundColor: uni('bg-static-content-default'),
  border: `1px solid ${uni('border-static-content-default')}`,
  borderRadius: uni('border-radius-md'),
}));

export const AdditionalInfoHeader = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-100'),
}));

export const AdditionalInfoTitle = styled('h2')`
  ${uniTypography('text-lg', 'medium')}
  color: ${uni('fg-neutral-static-default-heading')};
  margin: 0;
`;

export const AdditionalInfoSubtitle = styled('p')`
  ${uniTypography('text-sm', 'medium')}
  color: ${uni('fg-neutral-static-default-body')};
  margin: 0;
`;

export const SaleAccordionRoot = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('spacing-24-16'),
  padding: uni('padding-card-components'),
  backgroundColor: uni('bg-static-content-highlight'),
  borderRadius: uni('border-radius-xs'),

  // The design system's accordion brings MUI's layout underneath — a Paper with a
  // shadow, summary gutters, details padding — plus a wrapper of its own between the
  // summary and the content. The measurements below are the design's, applied over the
  // classes the library exports; without them the header sits at a different indent
  // from the tariff cards.
  [`& .${accordionClasses.root}`]: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    // From `md` up the library caps the accordion at a fixed 35rem; here it has to
    // follow the card it lives in, which is narrower on some viewports and wider on
    // others.
    maxWidth: '100%',
    // MUI's accordion is a rounded Paper; here it has no radius, since what rounds is
    // the block around it.
    borderRadius: 0,
    // The rule sits under the whole block, not under the header, which is why the
    // library's showBorderBottom does not serve here: it marks the summary.
    borderBottom: `1px solid ${uni('border-neutral-subtle-rest')}`,
  },

  [`& .${accordionSummaryClasses.root}`]: {
    padding: uni('scale-200'),
    minHeight: 'auto',
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: 'transparent' },
    [`&.${accordionClasses.expanded}`]: { minHeight: 'auto', backgroundColor: 'transparent' },
  },

  [`& .${accordionSummaryClasses.content}`]: {
    margin: 0,
    alignItems: 'flex-start',
    [`&.${accordionClasses.expanded}`]: { margin: 0 },

    // The title and meta wrapper is an item of a flex row: without growing it shrinks
    // to its content and the chip never reaches the right edge.
    '& > div': {
      flex: '1 0 0',
      minWidth: 0,
      gap: uni('scale-300'),
    },
  },

  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    padding: uni('scale-200'),
    color: uni('fg-neutral-action-default-rest'),
    // The accordion pins the chevron at 20px; the design uses 16.
    '& svg': { width: '16px', height: '16px' },
  },

  [`& .${accordionDetailsClasses.root}`]: {
    padding: uni('scale-200'),
  },
}));

/** Header row: the product name takes the line, the chip sits next to the chevron. */
export const SaleAccordionTitleRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: uni('scale-200'),
  width: '100%',
}));

export const SaleAccordionTitle = styled('span')`
  ${uniTypography('text-md', 'semibold')}
  color: ${uni('fg-neutral-action-default-rest')};
  flex: 1 0 0;
  min-width: 0;
`;

export const SaleAccordionMeta = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-200'),
}));

export const SaleAccordionMetaRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: uni('scale-200'),

  // The design tightens the seller row: avatar and name sit 4px apart, while date and
  // time sit 8px from each other.
  '&[data-tight="true"]': {
    gap: uni('scale-100'),
  },
}));

export const SaleAccordionMetaText = styled('span')`
  ${uniTypography('text-sm', 'medium')}
  color: ${uni('fg-neutral-static-default-body')};
  display: inline-flex;
  align-items: center;
  gap: ${uni('scale-100')};
`;

export const SellerAvatar = styled('img')(() => ({
  width: '20px',
  height: '20px',
  objectFit: 'cover',
  borderRadius: uni('border-radius-round'),
  border: `1px solid ${uni('border-neutral-subtle-rest')}`,
}));

export const TariffList = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-400'),
}));

export const TariffRowRoot = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: uni('scale-200'),
  padding: uni('padding-card-components'),
  backgroundColor: uni('bg-static-content-default'),
  borderRadius: uni('border-radius-xs'),
  [tabletBreakpoint]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

export const TariffRowLabels = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 0 0',
  minWidth: 0,
}));

export const TariffRowName = styled('span')`
  ${uniTypography('text-sm', 'semibold')}
  color: ${uni('fg-neutral-static-default-body')};
`;

export const TariffRowStatus = styled('span')`
  ${uniTypography('text-sm', 'medium')}
  color: ${uni('fg-neutral-static-default-caption')};

  &[data-tone='error'] {
    color: ${uni('fg-alert-default-rest')};
  }
`;

export const AdditionalInfoFooter = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: uni('scale-400'),
  [tabletBreakpoint]: {
    flexDirection: 'column-reverse',
    alignItems: 'stretch',
  },
}));

/**
 * Spacing between modal fields. `spacing-24-16` is the responsive token the design uses
 * here — 24 on desktop, 16 on mobile — not a fixed value.
 */
export const MODAL_FIELD_SPACING = uni('spacing-24-16');

/**
 * Editing stacks every form of the row. The scroll lives in the content, not in the
 * page, so the modal keeps the height the design fixed.
 */
export const ModalStackedContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  // The container carries the padding and the gap, the form goes clean, and the divider
  // is an element between them rather than a border.
  gap: uni('scale-600'),
  padding: uni('padding-content'),
  width: '100%',
  maxHeight: '440px',
  overflowY: 'auto',
}));

export const ModalTariffBlock = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('spacing-24-16'),
  width: '100%',
}));

export const ModalTariffLine = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: uni('scale-200'),
}));

/** The design uses text-sm regular on the modal header description. */
export const ModalTariffName = styled('span')`
  ${uniTypography('text-sm', 'regular')}
  color: ${uni('fg-neutral-static-default-body')};
`;

export const ModalFields = styled(Grid)(() => ({
  width: '100%',
  padding: uni('padding-content'),

  // When editing, the stacked container is what carries the padding.
  '&[data-flush="true"]': {
    padding: 0,
  },
}));

/** Half the row from `sm` up, the whole row below it. Theme breakpoints. */
export const tariffFieldSize = { xs: 12, sm: 6 } as const;

export const responsiveModalSx = {
  [`& .${modalActionsClasses.content}`]: {
    gap: uni('scale-300'),
  },

  [tabletBreakpoint]: {
    [`& .${modalClasses.content}`]: {
      position: 'fixed',
      inset: 'auto 0 0 0',
      width: '100%',
      maxWidth: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      borderRadius: `${uni('border-radius-md')} ${uni('border-radius-md')} 0 0`,
    },
    [`& .${modalActionsClasses.content}`]: {
      flexDirection: 'column-reverse',
      alignItems: 'stretch',
      '& button': {
        width: '100%',
      },
    },
  },
} as const;
